/**
 * The room search's two pickers, shared by the bar's popovers (`RoomSearchBar`) and the open form on an
 * empty tab (`RoomSearchPanels`).
 *
 * Dates: the first tap picks check-in, the second check-out; a tap on or before check-in starts again,
 * and past days cannot be picked.
 *
 * Guests are the whole party. Each child needs an age: hotels price children by age, so the picker asks
 * for one per child and the store refuses to search until every age is set.
 */
import type { Occupancy } from '@elara/domain/models/schemas/room-schemas';

import type { ChildAgeSlots } from './room-search-form.js';

import { format, parseISO, startOfToday } from 'date-fns';

import { pluralize } from '@elara/client-core/lib/utils';

import { Calendar } from '#ui/calendar.js';
import { Select, SelectItem } from '#ui/select.js';
import { Stepper } from '#ui/stepper.js';

const MAX_ADULTS = 8;
const MAX_CHILDREN = 6;
const CHILD_AGES = Array.from({ length: 18 }, (_, age) => age);

export interface StayCalendarProps {
  /** Check-in, YYYY-MM-DD. */
  startDate?: string;
  /** Check-out, YYYY-MM-DD. */
  endDate?: string;
  onDatesChange: (startDate: string | undefined, endDate: string | undefined) => void;
  /** Called once a check-out completes the stay, e.g. to close the popover holding the calendar. */
  onStayComplete?: () => void;
  /** Two side by side when the form is wide enough (`TWO_MONTHS_MIN_WIDTH`). */
  monthCount: 1 | 2;
}

export function StayCalendar({ startDate, endDate, onDatesChange, onStayComplete, monthCount }: StayCalendarProps) {
  function handleDayClick(day: Date) {
    // YYYY-MM-DD in the traveller's own calendar — never via UTC, which can land on the day before.
    const picked = format(day, 'yyyy-MM-dd');
    if (startDate && !endDate && picked > startDate) {
      onDatesChange(startDate, picked);
      onStayComplete?.();
      return;
    }
    onDatesChange(picked, undefined);
  }

  const selected = startDate ? { from: parseISO(startDate), to: endDate ? parseISO(endDate) : undefined } : undefined;
  return (
    <Calendar
      mode="range"
      // No floor under the weeks: `Calendar`'s `min-h-[307px]` left a gap below a five-week month, which
      // the open form's card shows (a popover's own padding hid it).
      className="min-h-0"
      // Side by side whenever the form is wide enough, not only on a wide viewport (`md:` in Calendar).
      classNames={monthCount === 2 ? { months: 'relative flex flex-row gap-6' } : undefined}
      defaultMonth={startDate ? parseISO(startDate) : undefined}
      selected={selected}
      onDayClick={handleDayClick}
      onSelect={() => {
        // Selection is driven by onDayClick, which knows which end of the stay a tap sets.
      }}
      disabled={(day: Date) => day < startOfToday()}
      numberOfMonths={monthCount}
    />
  );
}

export interface GuestPickerProps {
  occupancy: Occupancy;
  /** The form's per-child age slots (`useChildAgeSlots`), which outlive a popover. */
  slots: ChildAgeSlots;
}

export function GuestPicker({ occupancy, slots }: GuestPickerProps) {
  const { ages } = slots;
  return (
    // The rules between rows start under the text and run out through the surface's right padding to its
    // edge (`-mr-4`, each row padding itself back in with `pr-4`). Both surfaces it sits on, the open
    // form's panel and the bar's popover, are padded `p-4`.
    <div className="-mr-4 flex flex-col divide-y">
      <CountRow title="Adults" detail="Ages 18 or above">
        <Stepper value={occupancy.adults} min={1} max={MAX_ADULTS} onChange={slots.setAdults} />
      </CountRow>
      <CountRow title="Children" detail="Ages 0–17">
        <Stepper value={ages.length} min={0} max={MAX_CHILDREN} onChange={slots.setChildren} />
      </CountRow>
      {ages.length > 0 && (
        <div className="grid grid-cols-2 gap-2 py-4 pr-4 last:pb-0">
          {ages.map((age, i) => (
            <Select
              // Slots are positional: a child's age stays with its place in the list.
              key={`child-${i}`}
              value={age}
              placeholder={`Child ${i + 1} age`}
              onValueChange={(value) => slots.setChildAge(i, value)}
            >
              {CHILD_AGES.map((n) => (
                <SelectItem key={n} value={String(n)}>
                  {n === 0 ? 'Under 1' : `${n} ${pluralize(n, 'year')}`}
                </SelectItem>
              ))}
            </Select>
          ))}
        </div>
      )}
    </div>
  );
}

/** A guests row: what is being counted, and its stepper. */
function CountRow({ title, detail, children }: { title: string; detail?: string; children: React.ReactNode }) {
  return (
    <div className="flex items-center justify-between gap-4 py-3 pr-4 first:pt-0 last:pb-0">
      <div>
        <div className="text-sm font-medium">{title}</div>
        {detail && <div className="text-xs text-muted-foreground">{detail}</div>}
      </div>
      {children}
    </div>
  );
}
